import { fbt } from 'fbtee'
import { Loader2 } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { useNotificationPreferences } from '../../hooks/useNotificationPreferences'
import { pageTitle, useDocumentTitle } from '../../hooks/useDocumentTitle'
import { usePageLoadTrace } from '../../lib/perf'
import { track, type NotificationSetting } from '../../lib/analytics'
import { PageLayout } from '../../components/PageLayout'
import { SectionHeader } from '../../components/SectionHeader'
import { SettingsCard, SettingsRow } from '../../components/SettingsCard'
import { Switch } from '../../components/ui/switch'
import type { NotificationType } from '../../types'
import { EmailSection } from './EmailSection'

interface CategoryRow {
  key: NotificationType
  label: string
  description: string
}

const CATEGORIES: CategoryRow[] = [
  {
    key: 'group_invite',
    label: fbt('Group invites', 'Notification settings label for group invite notifications'),
    description: fbt(
      'When someone invites you to a group',
      'Notification settings description for group invite notifications',
    ),
  },
  {
    key: 'friend_request',
    label: fbt('Friend requests', 'Notification settings label for friend request notifications'),
    description: fbt(
      'When someone sends you a friend request',
      'Notification settings description for friend request notifications',
    ),
  },
  {
    key: 'set_post',
    label: fbt('Posts in sets', 'Notification settings label for set post notifications'),
    description: fbt(
      'When someone posts in a set you\'re interested in',
      'Notification settings description for set post notifications',
    ),
  },
  {
    key: 'event_linked',
    label: fbt('Events added to group', 'Notification settings label for event linked notifications'),
    description: fbt(
      'When someone adds an event to your group',
      'Notification settings description for event linked notifications',
    ),
  },
  {
    key: 'schedule_update',
    label: fbt('Schedule updates', 'Notification settings label for schedule update notifications'),
    description: fbt(
      'When a festival schedule is updated',
      'Notification settings description for schedule update notifications',
    ),
  },
  {
    key: 'event_cancelled',
    label: fbt('Cancelled events', 'Notification settings label for event cancellation notifications'),
    description: fbt(
      "When an event you're going to is called off",
      'Notification settings description for event cancellation notifications',
    ),
  },
  {
    key: 'ticket_update',
    label: fbt('Tickets', 'Notification settings label for ticket notifications'),
    description: fbt(
      'When someone gives you a ticket or answers about one of yours, and when your crew has a ticket you need or needs one you have',
      'Notification settings description for ticket notifications',
    ),
  },
  {
    key: 'artist_match',
    label: fbt('New events near you', 'Notification settings label for artist match notifications'),
    description: fbt(
      'When an artist you like is playing an event near you',
      'Notification settings description for artist match notifications',
    ),
  },
  {
    key: 'event_suggestion',
    label: fbt('Event suggestions', 'Notification settings label for event suggestion notifications'),
    description: fbt(
      'When an event you suggested is added',
      'Notification settings description for event suggestion notifications',
    ),
  },
  {
    key: 'request_addressed',
    label: fbt('Request updates', 'Notification settings label for request addressed notifications'),
    description: fbt(
      'When set times you reported are added, feedback you sent is shipped, or an area you asked for is planned',
      'Notification settings description for request addressed notifications',
    ),
  },
]

export default function NotificationSettingsPage() {
  useDocumentTitle(
    pageTitle(fbt('Notifications', 'Browser tab title for the notification settings page')),
  )
  const { user } = useAuth()
  const { prefs, loading, updatePreferences, toggleCategory, setEmailCategory } = useNotificationPreferences(user)

  function trackToggle(setting: NotificationSetting, enabled: boolean) {
    return () => track('notification_settings_changed', { setting, enabled })
  }

  usePageLoadTrace('notification_settings_page_to_content', !loading)

  return (
    <PageLayout
      back="/settings"
      title={
        <p className="font-semibold text-sm leading-none">
          <fbt desc="Heading for the notification settings page">Notifications</fbt>
        </p>
      }
    >
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-6">
          <section>
            <SectionHeader variant="settings">
              <fbt desc="Section heading for general notification settings">General</fbt>
            </SectionHeader>
            <SettingsCard>
              <SettingsRow
                label={fbt('Push notifications', 'Label for the master notification toggle')}
                description={fbt(
                  'Receive notifications on this device',
                  'Description for the master notification toggle',
                )}
                trailing={
                  <Switch
                    size="sm"
                    checked={prefs.enabled}
                    onCheckedChange={v => updatePreferences({ enabled: v }).then(trackToggle('all', v))}
                    className="cursor-pointer"
                  />
                }
              />
            </SettingsCard>
          </section>

          <section>
            <SectionHeader variant="settings">
              <fbt desc="Section heading for notification category settings">Categories</fbt>
            </SectionHeader>
            <SettingsCard>
              {CATEGORIES.map(cat => (
                <SettingsRow
                  key={cat.key}
                  label={cat.label}
                  description={cat.description}
                  className={!prefs.enabled ? 'opacity-50' : ''}
                  trailing={
                    <Switch
                      size="sm"
                      checked={prefs.categories[cat.key]}
                      onCheckedChange={v => toggleCategory(cat.key, v).then(trackToggle(cat.key, v))}
                      disabled={!prefs.enabled}
                      className="cursor-pointer shrink-0"
                    />
                  }
                />
              ))}
            </SettingsCard>
          </section>

          {user && !user.isAnonymous && <EmailSection prefs={prefs} setEmailCategory={setEmailCategory} />}
        </div>
      )}
    </PageLayout>
  )
}
